import React, {
	useContext,
	useEffect,
	useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import SelectSmall from '../../general-components/select-small';
import AudioMeter from '../../general-components/audio-meter';
import { GreenroomState as GS } from '../../../stores/types/types';
import useDeviceList from '../../../hooks/devices/use-device-list';
import Colors from '../../../utils/colors';
import {
	Button,
	ButtonLabel,
	PublisherContainer,
	VideoElContainer,
	VideoButtonsContainer,
	MuteButtonsContainer,
	RightPanelContainer,
	RightPanelHeader,
	DeviceSelectionContainer,
	RightPanelWrapper,
	ScrollContainer,
	RemoveEffectsButton,
	CheckboxButton,
} from './elements';
import type { OnsiteLocation } from '../../../stores/types/video';
import { OnsiteLocationSessionRoute } from '../../../stores/types/video';
import EffectsTab from '../../right-panel/user-menus/effects/effects';
import { selectVBG } from '../../../stores/actions/content';
import { OpenTokContext } from '../../../contexts/opentok/opentok-context';
import { setOnsiteLocationSession } from '../../../stores/actions/video';
import { UpdateOnsiteLocation } from '../../../connection/video';
import useKickLocationPublishers from './use-kick-location-publishers';

interface Props {
	onsiteLocation: OnsiteLocation;
	devicesChecked: boolean;
	handleOpenCanvas: () => void;
}

export default function OnsiteLocationPublishing(props: Props) {
	const {
		onsiteLocation,
		devicesChecked,
		handleOpenCanvas,
	} = props;

	const {
		id,
		safetyImage,
		hideSelfView,
		advancedAudio,
		resolution1080p,
	} = onsiteLocation;

	const dispatch = useDispatch();

	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const videoDevices = useSelector((s: GS) => s.VideoReducer.videoDevices);
	const audioDevices = useSelector((s: GS) => s.VideoReducer.audioDevices);
	const mainVideoStream = useSelector((s: GS) => s.VideoReducer.mainVideoStream);
	const selectedVBG = useSelector((s: GS) => s.ContentReducer.selectedVBG);
	const sessionInitialized = useSelector((s: GS) => s.OpenTokReducer.sessionInitialized);

	const [effectsOpen, setEffectsOpen] = useState<boolean>(false);

	useKickLocationPublishers();

	const {
		micOpts,
		videoOpts,
		selectedMicOption,
		selectedVideoOption,
	} = useDeviceList();

	const {
		forceFeedsTabRefresh,
		initialSetup,
		opentok,
		changeAudioDevice,
		changeVideoDevice,
		stopOT,
	} = useContext(OpenTokContext);

	const publisher = opentok ? opentok.publisher : null;
	const stream = publisher ? publisher.stream : null;
	const hasVideo = stream?.hasVideo ?? false;
	const hasAudio = stream?.hasAudio ?? false;

	function handleSelectMic(val: string) {
		const device = audioDevices.find((d: MediaDeviceInfo) => d.deviceId === val);
		if (device) {
			changeAudioDevice(device);
		}
	}

	function handleSelectCamera(val: string) {
		const device = videoDevices.find((d: MediaDeviceInfo) => d.deviceId === val);
		if (device) {
			changeVideoDevice(device);
		}
	}

	function handleDisconnect() {
		dispatch(setOnsiteLocationSession({
			id,
			route: OnsiteLocationSessionRoute.Entry,
		}));
	}

	function handleToggleSetting(updatedProps: Partial<OnsiteLocation>) {
		if (!token || !meeting) return;
		UpdateOnsiteLocation(
			token,
			meeting.meeting,
			id,
			updatedProps,
		);
	}

	useEffect(() => {
		if (
			devicesChecked
			&& !forceFeedsTabRefresh
			&& sessionInitialized
		) {
			initialSetup();
		}
	}, [
		devicesChecked,
		forceFeedsTabRefresh,
		sessionInitialized,
	]);

	useEffect(() => () => {
		stopOT();
	}, []);

	return (
		<>
			<PublisherContainer>
				<div>
					<VideoElContainer>
						<video
							muted
							autoPlay
							playsInline
							ref={(ref) => {
								if (mainVideoStream && ref && ref.srcObject !== mainVideoStream) {
									// eslint-disable-next-line no-param-reassign
									ref.srcObject = mainVideoStream;
								}
							}}
							style={{
								display: publisher?.stream?.hasVideo ? 'block' : 'none',
								height: '100%',
								maxHeight: '100%',
								maxWidth: '100%',
							}}
						/>
						<div className={classNames('poster', { active: !stream || !hasVideo })}>
							<img className="poster-image" src={safetyImage} alt={''} />
						</div>
					</VideoElContainer>
					<VideoButtonsContainer>
						<Button className="left" onClick={handleDisconnect}>
							<Icon name={ICONS.LEAVE} color={COLORS.SEMANTIC_RED_400} size={16} />
							<ButtonLabel className="semantic-red-400">Disconnect Feed</ButtonLabel>
						</Button>
						<MuteButtonsContainer>
							<Button
								className={classNames(stream && !hasVideo && 'off')}
								onClick={() => publisher?.publishVideo(!hasVideo)}
								disabled={!stream}
							>
								<Icon
									name={stream && hasVideo ? ICONS.CAMERA_ON : ICONS.CAMERA_OFF}
									color={stream && hasVideo ? COLORS.LIGHT_GRAY : COLORS.WHITE} size={20}
								/>
							</Button>
							<Button
								className={classNames(stream && !hasAudio && 'off')}
								onClick={() => publisher?.publishAudio(!hasAudio)}
								disabled={!stream}
							>
								<Icon
									name={stream && hasAudio ? ICONS.MICROPHONE_ON : ICONS.MICROPHONE_OFF}
									color={stream && hasAudio ? COLORS.LIGHT_GRAY : COLORS.WHITE} size={20}
								/>
							</Button>
						</MuteButtonsContainer>
						<Button
							className={classNames('right', effectsOpen && 'highlight-rainbow')}
							disabled={!stream}
							onClick={() => setEffectsOpen(!effectsOpen)}
						>
							<Icon name={ICONS.SPARKLE} color={COLORS.WHITE} size={16} />
							<ButtonLabel className="grayscale-200">Effects</ButtonLabel>
						</Button>
					</VideoButtonsContainer>
				</div>
				<RightPanelWrapper className="custom-scrollbar">
					<RightPanelContainer className={effectsOpen ? 'active' : 'not-active'}>
						<div className='justify-content-space-between margin-bottom-small'>
							<RightPanelHeader className="bl-font-header small-gradient">
								<div
									onClick={() => setEffectsOpen(false)}
									className='align-items-center'
									style={{ cursor: 'pointer' }}
								>
									<Icon name={ICONS.ARROW_LEFT} color={COLORS.HOT_PINK} size={24} />
									Effects
								</div>
							</RightPanelHeader>
							<RemoveEffectsButton disabled={!selectedVBG} onClick={() => {
								publisher?.clearVideoFilter();
								dispatch(selectVBG(null));
							}}>
								<Icon name={ICONS.CLOSE} color={COLORS.TEXT_GRAY_400} size={12}/>
								<span className='bl-font-l2'>Remove Effects</span>
							</RemoveEffectsButton>
						</div>
						<ScrollContainer className='feathered-scroll-container'>
							<EffectsTab hidePreview hidePopover />
						</ScrollContainer>
					</RightPanelContainer>
					<RightPanelContainer className={effectsOpen ? 'not-active' : 'active'}>
						<div>
							<RightPanelHeader>
								Choose your preferred<br />
								Camera and Microphone
							</RightPanelHeader>
							<DeviceSelectionContainer>
								<h6>
									<Icon name={ICONS.SOUND} color={COLORS.WHITE} size={16} />
									Sound
								</h6>
								<div className="option-row">
									<SelectSmall
										className={'onsite-publisher'}
										onChange={handleSelectMic}
										label={'Microphone'}
										dropdown={true}
										showLabel={true}
										invalid={!(audioDevices.length > 0)}
										disabled={!(audioDevices.length > 0)}
										optionHeight={40}
										options={micOpts}
										selected={selectedMicOption}
									/>
									<div className={classNames('option-row-buttons', audioDevices.length === 0 && 'disabled')}>
										<Icon name={ICONS.MICROPHONE_ON} size={20} color={COLORS.WHITE} />
										<AudioMeter
											inputStream={mainVideoStream ?? undefined}
											color={Colors.paleGreen}
										/>
									</div>
								</div>
							</DeviceSelectionContainer>
							<DeviceSelectionContainer>
								<h6>
									<Icon name={ICONS.CAMERA_ON} size={16} color={COLORS.WHITE} />
									Video
								</h6>
								<div className="option-row">
									<SelectSmall
										className={'onsite-publisher'}
										onChange={handleSelectCamera}
										label={'Selected Camera'}
										dropdown={true}
										showLabel={true}
										invalid={!(videoDevices.length > 0)}
										disabled={!(videoDevices.length > 0)}
										optionHeight={40}
										options={videoOpts}
										selected={selectedVideoOption}
									/>
								</div>
							</DeviceSelectionContainer>
						</div>
						<div className="justify-content-space-between" style={{gap: '6px'}}>
							<CheckboxButton className='onsite-chechbox-button' style={{width: '120px'}}>
								<input
									type="checkbox"
									checked={hideSelfView}
									onChange={() => handleToggleSetting({ hideSelfView: !hideSelfView })}
								/>
								Smart Canvas
							</CheckboxButton>
							<CheckboxButton className='onsite-chechbox-button' style={{width: '133px'}}>
								<input
									type="checkbox"
									checked={advancedAudio}
									onChange={() => handleToggleSetting({ advancedAudio: !advancedAudio })}
								/>
								Advanced Audio
							</CheckboxButton>
							<CheckboxButton className='onsite-chechbox-button' style={{width: '111px'}}>
								<input
									type="checkbox"
									checked={resolution1080p}
									onChange={() => handleToggleSetting({ resolution1080p: !resolution1080p })}
								/>
								Enable 1080p
							</CheckboxButton>
						</div>
					</RightPanelContainer>
				</RightPanelWrapper>
			</PublisherContainer>
			<div className="display-flex">
				<Button className="gradient" onClick={handleOpenCanvas}>
					<ButtonLabel className="grayscale-1200">Open Canvas in new window</ButtonLabel>
					<Icon name={ICONS.EXPAND} size={12} color={COLORS.TEXT_GRAY_1200} />
				</Button>
				<Button className="link">
					<a href="https://support.brandlive.com/hc/en-us/articles/35780874897179-Feature-Overview-Locations" rel="noopener noreferrer" target="_blank">
						Learn More
					</a>
				</Button>
			</div>
		</>
	);
}
